/**
 * Mindstepris.
 *
 * Mindsteprisen er hvad kunden mindst kommer til at betale for at have
 * produktet: engangsbeløbene plus abonnementet i hele bindingsperioden.
 * Bindingsperioden står på produktkonfigurationen som `commitment` — i
 * PantherAdmin hedder feltet `contractperiod`, "Bindingsperiode (i måneder)".
 * (`contractto` er noget andet: bindingens slutdato på et KØBT produkt.)
 *
 * Uden binding er mindsteprisen oprettelsen plus én måned. Nul måneder ville
 * give nul kroner, og det er ikke det tal kunden skal træffe sit valg på.
 *
 * Priserne er tidsserier, ikke komponenter: hver række har from/to, og kun de
 * rækker der gælder NU er produktets pris i dag. Det er samme regel som
 * kurven regner efter (basket.js), og de to skal blive ved med at være enige
 * — ellers står der ét beløb på kortet og et andet i kurven.
 */

/**
 * Bindingsperioden i måneder.
 *
 * @param {Object} product Produktkonfigurationen.
 * @return {number} Antal måneder, 0 når der ikke er binding.
 */
export function commitmentMonths( product ) {
    const raw = product && product.commitment;
    const months = parseInt( raw, 10 );

    return Number.isFinite( months ) && months > 0 ? months : 0;
}

/**
 * De prisrækker der gælder på et givet tidspunkt.
 *
 * to === -1 betyder "løber endnu". Sammenligningen er på tal, fordi API'et
 * leverer tidsstemplerne som strenge.
 *
 * @param {Array}  rows Prisrækker (subscriptions eller buys).
 * @param {number} now  Unix-sekunder.
 * @return {Array} De gyldige rækker.
 */
export function activeRows( rows, now ) {
    const t = Number( now ) || 0;

    return ( rows || [] ).filter( ( row ) => {
        if ( ! row ) {
            return false;
        }
        const from = Number( row.from );
        const to = Number( row.to );

        //from === -1 betyder "har altid gældt".
        const startet = ! Number.isFinite( from ) || from === -1 || from < t;
        const loeber = ! Number.isFinite( to ) || to === -1 || to > t;

        return startet && loeber;
    } );
}

/**
 * Prisen på en enkelt række efter rabat.
 *
 * Reglerne er de samme fire som kurven kender (basket.js). En ukendt regel
 * giver den fulde pris frem for nul: at gætte en rabat vi ikke forstår ville
 * love kunden noget vi ikke kan holde.
 *
 * @param {Object} row Prisrække.
 * @return {number} Prisen efter en eventuel rabat.
 */
export function rowPrice( row ) {
    const price = parseFloat( row && row.price ) || 0;
    const discount = row && row.discount;

    if ( ! discount ) {
        return price;
    }

    const value = parseFloat( discount.value ) || 0;

    switch ( discount.rule ) {
        case 'setprice':
            return value;
        case 'percent':
            return price - ( price * value / 100 );
        case 'amount':
        case 'line':
            return price - value * ( parseFloat( row.vat ) || 1 );
        default:
            return price;
    }
}

function sumRows( rows, now ) {
    return activeRows( rows, now ).reduce( ( total, row ) => total + rowPrice( row ), 0 );
}

/**
 * The product's prices as the card shows them: the subscription per month and
 * the one-off amount. One place, because the card, the label and the order
 * have to agree - an order remembering a different number than the customer
 * saw is worse than an order without a price.
 *
 * @param {Object} product Product from pa_products.
 * @return {{subscription: number, once: number}} Summed prices.
 */
export function productPrices( product ) {
    const sum = ( list ) => ( list || [] ).reduce(
        ( total, entry ) => total + Number( ( entry && entry.price ) || 0 ), 0
    );

    return {
        subscription: sum( product && product.subscriptions ),
        once: sum( product && product.buys ),
    };
}

/**
 * Mindsteprisen for ét produkt.
 *
 * @param {Object} product Produktkonfigurationen.
 * @param {number} now     Unix-sekunder. Udelades den, bruges nu.
 * @return {{months: number, monthly: number, once: number, total: number}}
 *         months er 0 uden binding; total regner da med én måned.
 */
export function minimumPrice( product, now ) {
    const t = Number( now ) || Math.floor( Date.now() / 1000 );
    const months = commitmentMonths( product );
    const monthly = sumRows( product && product.subscriptions, t );
    const once = sumRows( product && product.buys, t );

    return {
        months,
        monthly,
        once,
        total: once + monthly * Math.max( months, 1 ),
    };
}

/**
 * Beløb på dansk: hele kroner, tusindtalspunktum.
 *
 * Ører udelades med vilje. Mindsteprisen er et beslutningstal, og "3.588 kr"
 * læses hurtigere end "3.588,00 kr".
 *
 * @param {number} amount Beløbet.
 * @return {string} Formateret beløb uden enhed.
 */
export function formatKr( amount ) {
    const n = Math.round( Number( amount ) || 0 );

    return String( n ).replace( /\B(?=(\d{3})+(?!\d))/g, '.' );
}

/**
 * Mindsteprisen som én linje til kunden.
 *
 * Bindingen nævnes når der er en. Uden binding ville "i 0 måneder" være
 * forvirrende, og tallet er så bare første måned plus oprettelse.
 *
 * @param {Object} product Produktkonfigurationen.
 * @param {number} now     Unix-sekunder.
 * @return {string} Fx "Mindstepris 3.588 kr (6 mdr. binding)".
 */
export function minimumPriceText( product, now ) {
    const p = minimumPrice( product, now );

    if ( ! p.total ) {
        return '';
    }

    return p.months
        ? 'Mindstepris ' + formatKr( p.total ) + ' kr (' + p.months + ' mdr. binding)'
        : 'Mindstepris ' + formatKr( p.total ) + ' kr';
}
